import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { API_URL } from "../config";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Paper,
  InputAdornment,
  Pagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  QuestionMark as QuestionMarkIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  ImageOutlined as ImageOutlinedIcon,
  PictureAsPdfOutlined as PdfIcon,
  EmojiObjects as LightbulbIcon,
  UploadFile as UploadFileIcon,
  Person as PersonIcon,
  OpenInNew as OpenInNewIcon,
  Grade as GradeIcon,
  // AutoAwesome as AutoAwesomeIcon,
  LayersClear as LayersClearIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";

/* ─── Design Tokens ─────────────────────────────────────────────────── */
const t = {
  white: "#FFFFFF",
  bg: "#F5F6FA",
  surface: "#FFFFFF",
  surfaceRaised: "#FAFBFF",
  border: "rgba(99,102,180,0.13)",
  borderStrong: "rgba(99,102,180,0.25)",

  indigo: "#5B5FC7",
  indigoLight: "#EEEFFE",
  indigoDark: "#4248A8",
  indigoGhost: "rgba(91,95,199,0.08)",

  amber: "#D97706",
  amberLight: "#FEF3C7",
  amberMid: "#F59E0B",

  green: "#059669",
  greenLight: "#D1FAE5",

  rose: "#E11D48",
  roseLight: "#FFE4E6",

  slate: "#64748B",
  slateLight: "#F1F5F9",
  navy: "#1E2040",

  shadow: "0 1px 3px rgba(30,32,64,0.06), 0 4px 16px rgba(91,95,199,0.07)",
  shadowMd: "0 4px 24px rgba(91,95,199,0.12), 0 1px 4px rgba(30,32,64,0.06)",
  shadowLg: "0 12px 40px rgba(91,95,199,0.16), 0 2px 8px rgba(30,32,64,0.08)",
};

const sans = `'Inter', 'DM Sans', system-ui, sans-serif`;
const serif = `'Fraunces', 'Georgia', serif`;

/* ─── Animation Presets ─────────────────────────────────────────────── */
const ease = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.5, ease },
  }),
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const scaleUp = {
  hidden: { opacity: 0, scale: 0.96, y: 10 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.35, ease } },
  exit: { opacity: 0, scale: 0.96, y: 6, transition: { duration: 0.2 } },
};

const slideIn = {
  hidden: { opacity: 0, x: -12 },
  visible: (i = 0) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.07, duration: 0.4, ease },
  }),
};

/* ─── Reusable Atoms ────────────────────────────────────────────────── */
const Tag = ({
  children,
  color = t.indigoLight,
  textColor = t.indigo,
  icon,
}) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 1.2,
      py: 0.35,
      borderRadius: "6px",
      bgcolor: color,
      color: textColor,
      fontSize: 11,
      fontWeight: 600,
      fontFamily: sans,
      letterSpacing: 0.3,
    }}
  >
    {icon && <Box sx={{ fontSize: 11, display: "flex" }}>{icon}</Box>}
    {children}
  </Box>
);

const Divider2 = () => <Box sx={{ height: "1px", bgcolor: t.border, my: 2 }} />;

/* ─── Stat Card ─────────────────────────────────────────────────────── */
const StatCard = ({ label, value, icon, color, bgColor, custom }) => (
  <motion.div
    variants={fadeUp}
    initial="hidden"
    animate="visible"
    custom={custom}
  >
    <Paper
      elevation={0}
      sx={{
        borderRadius: "16px",
        p: 2.5,
        border: `1px solid ${t.border}`,
        bgcolor: t.surface,
        boxShadow: t.shadow,
        display: "flex",
        alignItems: "center",
        gap: 2,
        flex: "1 1 150px",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "12px",
          bgcolor: bgColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color,
          fontSize: 20,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: sans,
            fontSize: 11,
            fontWeight: 600,
            color: t.slate,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            mb: 0.3,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontFamily: serif,
            fontSize: 22,
            color: t.navy,
            fontWeight: 500,
            lineHeight: 1,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Paper>
  </motion.div>
);

/* ─── Question Card ─────────────────────────────────────────────────── */
const QuestionCard = ({ q, index, onEdit, onDelete }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      variants={slideIn}
      initial="hidden"
      animate="visible"
      custom={index % 5}
    >
      <Paper
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        elevation={0}
        sx={{
          border: `1px solid ${hovered ? t.indigo : t.border}`,
          borderRadius: "16px",
          p: "0",
          mb: 2,
          bgcolor: t.surface,
          overflow: "hidden",
          transition: "border-color 0.2s, box-shadow 0.2s",
          boxShadow: hovered ? t.shadowMd : t.shadow,
        }}
      >
        {/* Top accent bar */}
        <Box
          sx={{
            height: 3,
            background: `linear-gradient(90deg, ${t.indigo} 0%, ${t.amberMid} 100%)`,
            opacity: hovered ? 1 : 0,
            transition: "opacity 0.2s",
          }}
        />

        <Box sx={{ p: 2.5 }}>
          {/* Header row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                bgcolor: t.indigoLight,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontFamily: sans,
                  fontSize: 12,
                  fontWeight: 700,
                  color: t.indigo,
                }}
              >
                {index + 1}
              </Typography>
            </Box>
            <Tag>Question</Tag>
            {q.question_image && (
              <Tag color={t.amberLight} textColor={t.amber}>
                Image
              </Tag>
            )}
            {q.question_pdf && (
              <Tag color="#F3E8FF" textColor="#7C3AED">
                PDF
              </Tag>
            )}
          </Box>

          {/* Question text */}
          <Typography
            sx={{
              fontFamily: sans,
              fontWeight: 500,
              fontSize: 14.5,
              color: t.navy,
              mb: 1.5,
              lineHeight: 1.65,
            }}
          >
            {q.Question}
          </Typography>

          {/* Question image */}
          {q.question_image && (
            <Box
              sx={{
                mb: 2,
                borderRadius: "10px",
                overflow: "hidden",
                border: `1px solid ${t.border}`,
              }}
            >
              <img
                src={q.question_image}
                alt="Question"
                style={{
                  width: "100%",
                  maxHeight: 200,
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          )}

          {/* Question PDF */}
          {q.question_pdf && (
            <Button
              href={q.question_pdf}
              target="_blank"
              size="small"
              startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              sx={{
                fontFamily: sans,
                fontSize: 12,
                fontWeight: 600,
                color: "#7C3AED",
                bgcolor: "#F3E8FF",
                borderRadius: "8px",
                textTransform: "none",
                mb: 1.5,
                "&:hover": { bgcolor: "#EDE9FE" },
              }}
            >
              View PDF
            </Button>
          )}

          {/* Answer */}
          <Box
            sx={{
              bgcolor: t.slateLight,
              borderRadius: "10px",
              p: 1.5,
              mb: 2,
              borderLeft: `3px solid ${t.indigo}`,
            }}
          >
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <LightbulbIcon
                sx={{
                  fontSize: 15,
                  color: t.amberMid,
                  mt: 0.25,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontFamily: sans,
                  fontSize: 13.5,
                  color: t.slate,
                  lineHeight: 1.7,
                }}
              >
                {q.answer}
              </Typography>
            </Box>
          </Box>

          {/* Answer image */}
          {q.answer_image && (
            <Box
              sx={{
                mb: 2,
                borderRadius: "10px",
                overflow: "hidden",
                border: `1px solid ${t.border}`,
              }}
            >
              <img
                src={q.answer_image}
                alt="Answer"
                style={{
                  width: "100%",
                  maxHeight: 200,
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 14 }} />}
              onClick={() => onEdit(q)}
              sx={{
                fontFamily: sans,
                fontWeight: 600,
                fontSize: 12,
                color: t.indigo,
                bgcolor: t.indigoLight,
                borderRadius: "8px",
                px: 1.8,
                textTransform: "none",
                "&:hover": { bgcolor: t.indigoGhost },
              }}
            >
              Edit
            </Button>
            <Button
              size="small"
              startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
              onClick={() => onDelete(q.id)}
              sx={{
                fontFamily: sans,
                fontWeight: 600,
                fontSize: 12,
                color: t.rose,
                bgcolor: t.roseLight,
                borderRadius: "8px",
                px: 1.8,
                textTransform: "none",
                "&:hover": { bgcolor: "#FECDD3" },
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Paper>
    </motion.div>
  );
};

/* ─── File Upload Button ─────────────────────────────────────────────── */
const FileUploadBtn = ({
  label,
  accept,
  onChange,
  color,
  bgColor,
  hoverBg,
  icon,
}) => (
  <Button
    component="label"
    size="small"
    startIcon={icon}
    sx={{
      fontFamily: sans,
      fontWeight: 600,
      fontSize: 12,
      color,
      bgcolor: bgColor,
      borderRadius: "8px",
      textTransform: "none",
      px: 1.8,
      "&:hover": { bgcolor: hoverBg },
    }}
  >
    {label}
    <input
      type="file"
      hidden
      accept={accept}
      onChange={(e) => onChange(e.target.files[0])}
    />
  </Button>
);

/* ─── Attached File Chip ─────────────────────────────────────────────── */
const AttachedChip = ({ label, file, onClear }) => (
  <AnimatePresence>
    {file && (
      <motion.span
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.85 }}
        transition={{ duration: 0.18 }}
      >
        <Chip
          label={
            typeof file === "string" ? label : file.name?.slice(0, 20) || label
          }
          onDelete={onClear}
          size="small"
          sx={{
            fontFamily: sans,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: t.indigoLight,
            color: t.indigo,
            height: 26,
            "& .MuiChip-deleteIcon": { color: t.indigo, fontSize: 14 },
          }}
        />
      </motion.span>
    )}
  </AnimatePresence>
);

/* ─── Modal ──────────────────────────────────────────────────────────── */
const QuestionModal = ({
  open,
  onClose,
  onSave,
  selectedQuestion,
  question,
  setQuestion,
  answer,
  setAnswer,
  questionImage,
  setQuestionImage,
  questionPdf,
  setQuestionPdf,
  answerImage,
  setAnswerImage,
  savingQuestion,
}) => {
  const isEdit = !!selectedQuestion;

  const preview = (f) => {
    if (!f) return "";
    if (typeof f === "string") return f;
    try {
      return URL.createObjectURL(f);
    } catch {
      return "";
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="bd"
            variants={fadeIn}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1200,
              background: "rgba(15,17,36,0.5)",
              backdropFilter: "blur(8px)",
            }}
          />

          {/* Modal */}
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <motion.div
              key="modal"
              variants={scaleUp}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
              style={{ width: "100%", maxWidth: 560 }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "20px",
                  overflow: "hidden",
                  border: `1px solid ${t.borderStrong}`,
                  boxShadow: t.shadowLg,
                  maxHeight: "92vh",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: t.surface,
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    px: 3,
                    py: 2.5,
                    bgcolor: t.navy,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "10px",
                        bgcolor: "rgba(255,255,255,0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <QuestionMarkIcon sx={{ fontSize: 18, color: "#fff" }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: serif,
                          fontSize: 17,
                          color: "#fff",
                          fontWeight: 500,
                        }}
                      >
                        {isEdit ? "Edit Question" : "Add New Question"}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: sans,
                          fontSize: 12,
                          color: "rgba(255,255,255,0.5)",
                          mt: 0.1,
                        }}
                      >
                        {isEdit
                          ? "Modify the question below"
                          : "Fill in details to add a question"}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    onClick={onClose}
                    sx={{
                      color: "rgba(255,255,255,0.6)",
                      "&:hover": {
                        color: "#fff",
                        bgcolor: "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Body */}
                <Box
                  sx={{
                    px: 3,
                    py: 2.5,
                    overflowY: "auto",
                    maxHeight: "calc(92vh - 90px)",
                    bgcolor: t.surfaceRaised,
                  }}
                >
                  {/* Question */}
                  <Typography
                    sx={{
                      fontFamily: sans,
                      fontWeight: 600,
                      fontSize: 11,
                      color: t.slate,
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      mb: 0.8,
                    }}
                  >
                    Question
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Type your question here…"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    sx={{
                      mb: 1.5,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        fontFamily: sans,
                        fontSize: 14,
                        bgcolor: t.white,
                        "& fieldset": { borderColor: t.border },
                        "&:hover fieldset": { borderColor: t.borderStrong },
                        "&.Mui-focused fieldset": {
                          borderColor: t.indigo,
                          borderWidth: "1.5px",
                        },
                      },
                    }}
                  />

                  {questionImage && (
                    <Box
                      sx={{
                        mb: 2,
                        borderRadius: "10px",
                        overflow: "hidden",
                        border: `1px solid ${t.border}`,
                        bgcolor: t.slateLight,
                      }}
                    >
                      <img
                        src={preview(questionImage)}
                        alt=""
                        style={{
                          width: "100%",
                          maxHeight: 180,
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    </Box>
                  )}
                  {questionPdf && (
                    <Box
                      sx={{
                        mb: 2,
                        borderRadius: "10px",
                        overflow: "hidden",
                        border: `1px solid ${t.border}`,
                      }}
                    >
                      <iframe
                        src={preview(questionPdf)}
                        width="100%"
                        height="220"
                        title="PDF Preview"
                        style={{ border: "none", display: "block" }}
                      />
                    </Box>
                  )}

                  {/* Answer */}
                  <Typography
                    sx={{
                      fontFamily: sans,
                      fontWeight: 600,
                      fontSize: 11,
                      color: t.slate,
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      mb: 0.8,
                    }}
                  >
                    Expected Answer
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Enter the expected answer…"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    sx={{
                      mb: 1.5,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        fontFamily: sans,
                        fontSize: 14,
                        bgcolor: t.white,
                        "& fieldset": { borderColor: t.border },
                        "&:hover fieldset": { borderColor: t.borderStrong },
                        "&.Mui-focused fieldset": {
                          borderColor: t.indigo,
                          borderWidth: "1.5px",
                        },
                      },
                    }}
                  />

                  {answerImage && (
                    <Box
                      sx={{
                        mb: 2,
                        borderRadius: "10px",
                        overflow: "hidden",
                        border: `1px solid ${t.border}`,
                        bgcolor: t.slateLight,
                      }}
                    >
                      <img
                        src={preview(answerImage)}
                        alt=""
                        style={{
                          width: "100%",
                          maxHeight: 180,
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    </Box>
                  )}

                  {/* Attachments */}
                  <Divider2 />
                  <Typography
                    sx={{
                      fontFamily: sans,
                      fontWeight: 600,
                      fontSize: 11,
                      color: t.slate,
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      mb: 1.2,
                    }}
                  >
                    Attachments
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1.2,
                      p: 2,
                      borderRadius: "12px",
                      border: `1.5px dashed ${t.borderStrong}`,
                      bgcolor: t.white,
                    }}
                  >
                    <FileUploadBtn
                      label="Q. Image"
                      accept="image/*"
                      onChange={setQuestionImage}
                      color={t.indigo}
                      bgColor={t.indigoLight}
                      hoverBg={t.indigoGhost}
                      icon={<ImageOutlinedIcon sx={{ fontSize: 14 }} />}
                    />
                    <FileUploadBtn
                      label="Q. PDF"
                      accept="application/pdf"
                      onChange={setQuestionPdf}
                      color="#7C3AED"
                      bgColor="#F3E8FF"
                      hoverBg="#EDE9FE"
                      icon={<PdfIcon sx={{ fontSize: 14 }} />}
                    />
                    <FileUploadBtn
                      label="A. Image"
                      accept="image/*"
                      onChange={setAnswerImage}
                      color={t.green}
                      bgColor={t.greenLight}
                      hoverBg="#A7F3D0"
                      icon={<ImageOutlinedIcon sx={{ fontSize: 14 }} />}
                    />

                    {(questionImage || questionPdf || answerImage) && (
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          pt: 0.5,
                        }}
                      >
                        <AttachedChip
                          label="Question Image"
                          file={questionImage}
                          onClear={() => setQuestionImage(null)}
                        />
                        <AttachedChip
                          label="Question PDF"
                          file={questionPdf}
                          onClear={() => setQuestionPdf(null)}
                        />
                        <AttachedChip
                          label="Answer Image"
                          file={answerImage}
                          onClear={() => setAnswerImage(null)}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Footer actions */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 1.5,
                      mt: 3,
                    }}
                  >
                    <Button
                      onClick={onClose}
                      sx={{
                        fontFamily: sans,
                        fontWeight: 600,
                        fontSize: 13,
                        color: t.slate,
                        bgcolor: t.slateLight,
                        borderRadius: "10px",
                        px: 3,
                        textTransform: "none",
                        "&:hover": { bgcolor: "#E2E8F0" },
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={onSave}
                      disabled={savingQuestion}
                      disableElevation
                      sx={{
                        fontFamily: sans,
                        fontWeight: 700,
                        fontSize: 13,
                        bgcolor: t.indigo,
                        color: "#fff",
                        borderRadius: "10px",
                        px: 3.5,
                        textTransform: "none",
                        minWidth: 130,
                        "&:hover": { bgcolor: t.indigoDark },
                        "&.Mui-disabled": { bgcolor: t.border, color: t.slate },
                      }}
                    >
                      {savingQuestion ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : isEdit ? (
                        "Update"
                      ) : (
                        "Save Question"
                      )}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </motion.div>
          </Box>
        </>
      )}
    </AnimatePresence>
  );
};

/* ─── Submission Row ─────────────────────────────────────────────────── */
const SubmissionRow = ({ submission, score, onScoreChange, onSubmit }) => (
  <motion.div variants={slideIn} initial="hidden" animate="visible">
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${t.border}`,
        borderRadius: "14px",
        p: 2,
        mb: 1.5,
        bgcolor: t.surface,
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
        boxShadow: t.shadow,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          bgcolor: t.indigoLight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <PersonIcon sx={{ fontSize: 18, color: t.indigo }} />
      </Box>

      <Typography
        sx={{
          fontFamily: sans,
          fontWeight: 600,
          fontSize: 14,
          color: t.navy,
          flex: 1,
          minWidth: 120,
        }}
      >
        {submission.student_name}
      </Typography>

      {submission.score !== undefined && submission.score !== null && (
        <Tag
          color={t.greenLight}
          textColor={t.green}
          icon={<GradeIcon sx={{ fontSize: 11 }} />}
        >
          Score: {submission.score}
        </Tag>
      )}

      <Button
        href={submission.pdf}
        target="_blank"
        size="small"
        startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
        sx={{
          fontFamily: sans,
          fontWeight: 600,
          fontSize: 12,
          color: "#7C3AED",
          bgcolor: "#F3E8FF",
          borderRadius: "8px",
          textTransform: "none",
          "&:hover": { bgcolor: "#EDE9FE" },
        }}
      >
        View PDF
      </Button>

      <TextField
        placeholder="Score"
        type="number"
        size="small"
        value={score || ""}
        onChange={(e) => onScoreChange(e.target.value)}
        sx={{
          width: 90,
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            fontFamily: sans,
            fontSize: 13,
            "& fieldset": { borderColor: t.border },
            "&:hover fieldset": { borderColor: t.borderStrong },
            "&.Mui-focused fieldset": { borderColor: t.indigo },
          },
        }}
      />

      <Button
        onClick={onSubmit}
        size="small"
        disabled={!score}
        sx={{
          fontFamily: sans,
          fontWeight: 600,
          fontSize: 12,
          bgcolor: t.indigo,
          color: "#fff",
          borderRadius: "8px",
          textTransform: "none",
          px: 2,
          "&:hover": { bgcolor: t.indigoDark },
          "&.Mui-disabled": { bgcolor: t.border, color: t.slate },
        }}
      >
        Submit
      </Button>
    </Paper>
  </motion.div>
);

/* ─── Empty State ────────────────────────────────────────────────────── */
const EmptyState = ({ icon, title, sub, action }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: "18px",
      border: `2px dashed ${t.border}`,
      p: 5,
      textAlign: "center",
      bgcolor: t.surface,
    }}
  >
    <Box
      sx={{
        width: 60,
        height: 60,
        borderRadius: "16px",
        bgcolor: t.indigoLight,
        mx: "auto",
        mb: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        fontFamily: serif,
        fontSize: 18,
        color: t.navy,
        mb: 0.5,
        fontWeight: 500,
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontFamily: sans,
        fontSize: 13,
        color: t.slate,
        mb: action ? 2.5 : 0,
      }}
    >
      {sub}
    </Typography>
    {action}
  </Paper>
);

/* ─── Main Page ──────────────────────────────────────────────────────── */
const InstAssignmentPage = () => {
  const [marks, setMarks] = useState("");
  const [page, setPage] = useState(1);
  const [assignmentId, setAssignmentId] = useState(null);
  const [assignmentData, setAssignmentData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [questionImage, setQuestionImage] = useState(null);
  const [questionPdf, setQuestionPdf] = useState(null);
  const [answerImage, setAnswerImage] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [scores, setScores] = useState({});
  const [creatingAssignment, setCreatingAssignment] = useState(false);
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [loadingAssignment, setLoadingAssignment] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const { subId } = useParams();
  const subjectName = location.state?.subjectName;
  const PER_PAGE = 5;
  const totalPages = Math.ceil(questions.length / PER_PAGE);
  const sliced = questions.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  useEffect(() => {
    if (page > totalPages && totalPages > 0) setPage(totalPages);
  }, [questions.length, totalPages, page]);

  const fetchAssignment = async () => {
    setLoadingAssignment(true);
    try {
      const token = JSON.parse(localStorage.getItem("authToken"));
      const res = await fetch(`${API_URL}/assignment-mark/${subId}/`, {
        headers: { Authorization: `Bearer ${token.access}` },
      });
      const data = await res.json();
      if (res.ok && data.assignments?.length > 0 && data.marks?.length > 0) {
        const a = data.assignments[data.assignments.length - 1];
        const m = data.marks[data.marks.length - 1];
        setAssignmentId(a.id);
        setAssignmentData({
          id: m.id,
          activityItemId: a.activity_item_id,
          marks: m.marks,
        });
        setMarks(m.marks);
      }
    } catch {
    } finally {
      setLoadingAssignment(false);
    }
  };
  console.log(loadingAssignment);
  const fetchQuestions = async () => {
    if (!assignmentId) return;
    try {
      const token = JSON.parse(localStorage.getItem("authToken"));
      const res = await fetch(
        `${API_URL}/assignments/${assignmentId}/questions/`,
        {
          headers: { Authorization: `Bearer ${token.access}` },
        }
      );
      const data = await res.json();
      setQuestions(data);
    } catch {}
  };

  const fetchSubmissions = async () => {
    if (!assignmentId) return;
    try {
      const token = JSON.parse(localStorage.getItem("authToken"));
      const res = await fetch(
        `${API_URL}/assignment-submissions/${assignmentId}/`,
        {
          headers: { Authorization: `Bearer ${token.access}` },
        }
      );
      const data = await res.json();
      if (res.ok) setSubmissions(data);
    } catch {}
  };

  useEffect(() => {
    fetchAssignment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subId]);
  useEffect(() => {
    if (assignmentId) {
      fetchQuestions();
      fetchSubmissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId]);

  const handleCreate = async () => {
    setCreatingAssignment(true);
    try {
      const token = JSON.parse(localStorage.getItem("authToken"));
      const res = await fetch(`${API_URL}/assignment-mark/${subId}/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token.access}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ marks: Number(marks) }),
      });
      const data = await res.json();
      if (res.ok) {
        setAssignmentId(data.assignment_id);
        setAssignmentData({
          title: data.assignment_title,
          activityItemId: data.activity_item_id,
          marks: data.marks.marks,
        });
        toast.success("Assignment created!");
      } else toast.error(data.error || "Failed");
    } catch {
      toast.error("Network error");
    } finally {
      setCreatingAssignment(false);
    }
  };

  const handleUpdate = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("authToken"));
      const res = await fetch(
        `${API_URL}/assignment-mark/${subId}/${assignmentData.id}/`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token.access}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ marks: Number(marks) }),
        }
      );
      if (res.ok) {
        toast.success("Assignment updated");
        fetchAssignment();
      } else {
        const d = await res.json();
        toast.error(d.detail);
      }
    } catch {
      toast.error("Network error");
    }
  };

  const handleDelete = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("authToken"));
      const res = await fetch(
        `${API_URL}/assignment-mark/${subId}/${assignmentData.id}/`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token.access}` },
        }
      );
      if (res.ok) {
        toast.success("Assignment removed");
        setAssignmentId(null);
        setAssignmentData(null);
        setQuestions([]);
        setMarks("");
        fetchAssignment();
      } else {
        const d = await res.json();
        toast.error(d.detail);
      }
    } catch {
      toast.error("Network error");
    }
  };

  const handleSaveQuestion = async () => {
    setSavingQuestion(true);
    try {
      const token = JSON.parse(localStorage.getItem("authToken"));
      const fd = new FormData();
      fd.append("assignment", assignmentId);
      fd.append("Question", question);
      fd.append("answer", answer);
      if (questionImage) fd.append("question_image", questionImage);
      if (questionPdf) fd.append("question_pdf", questionPdf);
      if (answerImage) fd.append("answer_image", answerImage);
      const url = selectedQuestion
        ? `${API_URL}/assignments/${assignmentId}/questions/${selectedQuestion.id}/`
        : `${API_URL}/assignments/${assignmentId}/questions/`;
      const res = await fetch(url, {
        method: selectedQuestion ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${token.access}` },
        body: fd,
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(selectedQuestion ? "Question updated" : "Question added");
        setQuestion("");
        setAnswer("");
        setQuestionImage(null);
        setQuestionPdf(null);
        setAnswerImage(null);
        setSelectedQuestion(null);
        setOpenModal(false);
        fetchQuestions();
      } else toast.error(data.error || data.detail || "Failed");
    } catch {
      toast.error("Network error");
    } finally {
      setSavingQuestion(false);
    }
  };

  const handleDeleteQuestion = async (qId) => {
    try {
      const token = JSON.parse(localStorage.getItem("authToken"));
      const res = await fetch(
        `${API_URL}/assignments/${assignmentId}/questions/${qId}/`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token.access}` },
        }
      );
      if (res.ok) {
        toast.success("Question deleted");
        fetchQuestions();
      } else toast.error("Delete failed");
    } catch {
      toast.error("Network error");
    }
  };

  const openEdit = (q) => {
    setSelectedQuestion(q);
    setQuestion(q.Question || "");
    setAnswer(q.answer || "");
    setQuestionImage(q.question_image || null);
    setQuestionPdf(q.question_pdf || null);
    setAnswerImage(q.answer_image || null);
    setOpenModal(true);
  };

  const openAdd = () => {
    setSelectedQuestion(null);
    setQuestion("");
    setAnswer("");
    setQuestionImage(null);
    setQuestionPdf(null);
    setAnswerImage(null);
    setOpenModal(true);
  };

  const scoreSubmission = async (id, score) => {
    try {
      const token = JSON.parse(localStorage.getItem("authToken"));
      const res = await fetch(`${API_URL}/score-assignment/${id}/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token.access}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ score: Number(score) }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Score submitted");
        fetchSubmissions();
      } else toast.error(data.detail);
    } catch {
      toast.error("Network error");
    }
  };

  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@400;500;700&family=Inter:wght@400;500;600;700&display=swap');`}</style>

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: t.bg,
          fontFamily: sans,
          py: 5,
          px: { xs: 2, sm: 4, md: 6 },
        }}
      >
        <Box sx={{ maxWidth: 840, mx: "auto" }}>
          {/* ── Page Header ── */}
          {/* Back button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            style={{ position: "absolute", top: 24, left: 24, zIndex: 10 }}
          >
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                color: "#475569",
                background: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(8px)",
                boxShadow:
                  "0 2px 12px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: "#fff",
                  color: "#0f172a",
                  transform: "translateX(-2px)",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                },
              }}
            >
              <FaArrowLeft size={15} />
            </IconButton>
          </motion.div>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 5,
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "14px",
                    bgcolor: t.indigo,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 8px 24px rgba(91,95,199,0.35)`,
                  }}
                >
                  <AssignmentIcon sx={{ fontSize: 24, color: "#fff" }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: serif,
                      fontSize: { xs: 24, sm: 30 },
                      color: t.navy,
                      fontWeight: 500,
                      lineHeight: 1.15,
                    }}
                  >
                    Assignment Dashboard
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: sans,
                      fontSize: 20,
                      color: t.slate,
                      mt: 0.3,
                    }}
                  >
                    Subject :{" "}
                    <strong style={{ color: t.indigo }}>{subjectName }</strong>
                  </Typography>
                </Box>
              </Box>

              {assignmentId && (
                <Tag
                  color={t.greenLight}
                  textColor={t.green}
                  icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                >
                  Active — #{assignmentId}
                </Tag>
              )}
            </Box>
          </motion.div>

          {/* ── Stats Row ── */}
          <AnimatePresence>
            {assignmentData && (
              <Box sx={{ display: "flex", gap: 2, mb: 4, flexWrap: "wrap" }}>
                <StatCard
                  label="Total Marks"
                  value={assignmentData.marks}
                  color={t.indigo}
                  bgColor={t.indigoLight}
                  icon={<StarIcon sx={{ fontSize: 20 }} />}
                  custom={0}
                />
                <StatCard
                  label="Questions"
                  value={questions.length}
                  color={t.green}
                  bgColor={t.greenLight}
                  icon={<QuestionMarkIcon sx={{ fontSize: 20 }} />}
                  custom={1}
                />
                <StatCard
                  label="Submissions"
                  value={submissions.length}
                  color={t.amber}
                  bgColor={t.amberLight}
                  icon={<UploadFileIcon sx={{ fontSize: 20 }} />}
                  custom={2}
                />
              </Box>
            )}
          </AnimatePresence>

          {/* ── Assignment Settings Card ── */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={2}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: "18px",
                border: `1px solid ${t.border}`,
                p: 3,
                mb: 4,
                bgcolor: t.surface,
                boxShadow: t.shadow,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <TuneIcon sx={{ fontSize: 18, color: t.indigo }} />
                <Typography
                  sx={{
                    fontFamily: serif,
                    fontSize: 18,
                    color: t.navy,
                    fontWeight: 500,
                  }}
                >
                  {assignmentId ? "Assignment Settings" : "Create Assignment"}
                </Typography>
              </Box>
              <Typography
                sx={{ fontFamily: sans, fontSize: 13, color: t.slate, mb: 2.5 }}
              >
                Set the total marks for this assignment
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  type="number"
                  placeholder="Total marks"
                  value={marks}
                  onChange={(e) => setMarks(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <StarIcon sx={{ fontSize: 16, color: t.amber }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: "1 1 180px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      fontFamily: sans,
                      bgcolor: t.slateLight,
                      "& fieldset": { borderColor: t.border },
                      "&:hover fieldset": { borderColor: t.borderStrong },
                      "&.Mui-focused fieldset": {
                        borderColor: t.indigo,
                        borderWidth: "1.5px",
                      },
                    },
                  }}
                />

                <Button
                  onClick={assignmentId ? handleUpdate : handleCreate}
                  disabled={creatingAssignment || !marks}
                  disableElevation
                  sx={{
                    fontFamily: sans,
                    fontWeight: 700,
                    fontSize: 13,
                    bgcolor: t.indigo,
                    color: "#fff",
                    borderRadius: "10px",
                    px: 3,
                    py: 1.4,
                    textTransform: "none",
                    minWidth: 160,
                    "&:hover": { bgcolor: t.indigoDark },
                    "&.Mui-disabled": { bgcolor: t.border, color: t.slate },
                  }}
                >
                  {creatingAssignment ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={15} color="inherit" />
                      {assignmentId ? "Updating…" : "Creating…"}
                    </Box>
                  ) : assignmentId ? (
                    "Update mark"
                  ) : (
                    "Create mark"
                  )}
                </Button>

                {assignmentId && (
                  <Button
                    onClick={handleDelete}
                    startIcon={<LayersClearIcon sx={{ fontSize: 15 }} />}
                    sx={{
                      fontFamily: sans,
                      fontWeight: 600,
                      fontSize: 13,
                      color: t.rose,
                      bgcolor: t.roseLight,
                      borderRadius: "10px",
                      px: 2.5,
                      py: 1.4,
                      textTransform: "none",
                      "&:hover": { bgcolor: "#FECDD3" },
                    }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            </Paper>
          </motion.div>

          {/* ── Questions Section ── */}
          <AnimatePresence>
            {assignmentId && (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography
                      sx={{
                        fontFamily: serif,
                        fontSize: 20,
                        color: t.navy,
                        fontWeight: 500,
                      }}
                    >
                      Questions
                    </Typography>
                    {questions.length > 0 && (
                      <Box
                        sx={{
                          minWidth: 22,
                          height: 22,
                          borderRadius: "6px",
                          bgcolor: t.indigoLight,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 0.8,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: sans,
                            fontSize: 11,
                            fontWeight: 700,
                            color: t.indigo,
                          }}
                        >
                          {questions.length}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Button
                    onClick={openAdd}
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    disableElevation
                    sx={{
                      fontFamily: sans,
                      fontWeight: 700,
                      fontSize: 13,
                      bgcolor: t.indigo,
                      color: "#fff",
                      borderRadius: "10px",
                      px: 2.5,
                      py: 1.1,
                      textTransform: "none",
                      "&:hover": { bgcolor: t.indigoDark },
                    }}
                  >
                    Add Question
                  </Button>
                </Box>

                {questions.length === 0 ? (
                  <EmptyState
                    icon={
                      <QuestionMarkIcon
                        sx={{ fontSize: 26, color: t.indigo }}
                      />
                    }
                    title="No questions yet"
                    sub="Add your first question to get started"
                    action={
                      <Button
                        onClick={openAdd}
                        startIcon={<AddIcon />}
                        sx={{
                          fontFamily: sans,
                          fontWeight: 600,
                          fontSize: 13,
                          color: t.indigo,
                          bgcolor: t.indigoLight,
                          borderRadius: "10px",
                          px: 3,
                          textTransform: "none",
                          "&:hover": { bgcolor: t.indigoGhost },
                        }}
                      >
                        Add First Question
                      </Button>
                    }
                  />
                ) : (
                  <>
                    {sliced.map((q, i) => (
                      <QuestionCard
                        key={q.id}
                        q={q}
                        index={(page - 1) * PER_PAGE + i}
                        onEdit={openEdit}
                        onDelete={handleDeleteQuestion}
                      />
                    ))}
                    {totalPages > 1 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mt: 3,
                          mb: 1,
                        }}
                      >
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={(_, v) => setPage(v)}
                          variant="outlined"
                          shape="rounded"
                          sx={{
                            "& .MuiPaginationItem-root": {
                              fontFamily: sans,
                              fontWeight: 600,
                              fontSize: 13,
                              borderColor: t.border,
                              color: t.slate,
                              borderRadius: "8px",
                              "&:hover": {
                                bgcolor: t.indigoLight,
                                color: t.indigo,
                              },
                              "&.Mui-selected": {
                                bgcolor: t.indigo,
                                color: "#fff",
                                borderColor: t.indigo,
                                "&:hover": { bgcolor: t.indigoDark },
                              },
                            },
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Submissions Section ── */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={4}
          >
            <Box sx={{ mt: 5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 2.5,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: serif,
                    fontSize: 20,
                    color: t.navy,
                    fontWeight: 500,
                  }}
                >
                  Student Submissions
                </Typography>
                {submissions.length > 0 && (
                  <Box
                    sx={{
                      minWidth: 22,
                      height: 22,
                      borderRadius: "6px",
                      bgcolor: t.amberLight,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 0.8,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: sans,
                        fontSize: 11,
                        fontWeight: 700,
                        color: t.amber,
                      }}
                    >
                      {submissions.length}
                    </Typography>
                  </Box>
                )}
              </Box>

              {submissions.length === 0 ? (
                <EmptyState
                  icon={
                    <AssignmentIcon sx={{ fontSize: 26, color: t.indigo }} />
                  }
                  title="No submissions yet"
                  sub="Student submissions will appear here once they submit their work"
                />
              ) : (
                submissions.map((sub) => (
                  <SubmissionRow
                    key={sub.id}
                    submission={sub}
                    score={scores[sub.id]}
                    onScoreChange={(v) =>
                      setScores((p) => ({ ...p, [sub.id]: v }))
                    }
                    onSubmit={() => scoreSubmission(sub.id, scores[sub.id])}
                  />
                ))
              )}
            </Box>
          </motion.div>
        </Box>
      </Box>

      {/* ── Modal ── */}
      <QuestionModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSaveQuestion}
        selectedQuestion={selectedQuestion}
        question={question}
        setQuestion={setQuestion}
        answer={answer}
        setAnswer={setAnswer}
        questionImage={questionImage}
        setQuestionImage={setQuestionImage}
        questionPdf={questionPdf}
        setQuestionPdf={setQuestionPdf}
        answerImage={answerImage}
        setAnswerImage={setAnswerImage}
        savingQuestion={savingQuestion}
      />
    </>
  );
};

export default InstAssignmentPage;
